import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { formatCurrency } from '@cleanpro/common-ui';
import { useSetPageMeta } from '../../../hooks/usePageMeta';
import type { PerNetworkReport, AdNetwork, AllocatorDecision } from '../types';
import {
  fetchPerNetworkReport,
  applyAllocationOverride,
  setNetworkPaused,
} from '../api';

// ─── Constants ──────────────────────────────────────────────────────────────

const NETWORK_LABELS: Record<AdNetwork, string> = {
  meta: 'Meta',
  google_ads: 'Google Ads',
  google_lsa: 'Google LSA',
};

const NETWORK_COLORS: Record<AdNetwork, string> = {
  meta: 'bg-blue-100 text-blue-700',
  google_ads: 'bg-green-100 text-green-700',
  google_lsa: 'bg-yellow-100 text-yellow-700',
};

// ─── Sub-components ─────────────────────────────────────────────────────────

function NetworkBadge({ network }: { network: AdNetwork }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${NETWORK_COLORS[network]}`}>
      {NETWORK_LABELS[network]}
    </span>
  );
}

function CampaignTable({ report }: { report: PerNetworkReport }) {
  const networks: AdNetwork[] = ['meta', 'google_ads', 'google_lsa'];

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">Campaigns by Network</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Network</th>
              <th className="px-4 py-3 text-left">Campaign</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Daily Budget</th>
              <th className="px-4 py-3 text-right">Total Spend</th>
              <th className="px-4 py-3 text-right">Leads</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {networks.map((network) => {
              const networkCampaigns = report.campaigns.filter((c) => c.network === network);
              if (networkCampaigns.length === 0) {
                return (
                  <tr key={network} className="text-gray-400">
                    <td className="px-4 py-3"><NetworkBadge network={network} /></td>
                    <td className="px-4 py-3 italic" colSpan={5}>No campaigns</td>
                  </tr>
                );
              }
              return networkCampaigns.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3"><NetworkBadge network={c.network} /></td>
                  <td className="px-4 py-3 font-medium text-gray-800">{c.name}</td>
                  <td className="px-4 py-3 capitalize text-gray-600">{c.status}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(c.dailyBudget)}/day</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(c.totalSpend)}</td>
                  <td className="px-4 py-3 text-right">{c.totalLeads}</td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CpaqTable({ report }: { report: PerNetworkReport }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800">Cost Per Accepted Quote (CPAQ)</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Network</th>
              <th className="px-4 py-3 text-right">Trailing 7d</th>
              <th className="px-4 py-3 text-right">Trailing 30d</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {report.cpaq.map((row) => (
              <tr key={row.network} className="hover:bg-gray-50">
                <td className="px-4 py-3"><NetworkBadge network={row.network} /></td>
                <td className="px-4 py-3 text-right">
                  {row.cpaqTrailing7d !== null ? formatCurrency(row.cpaqTrailing7d) : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {row.cpaqTrailing30d !== null ? formatCurrency(row.cpaqTrailing30d) : <span className="text-gray-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AllocatorLog({
  decisions,
  page,
  onPageChange,
}: {
  decisions: AllocatorDecision[];
  page: number;
  onPageChange: (p: number) => void;
}) {
  const PAGE_SIZE = 10;
  const totalPages = Math.ceil(decisions.length / PAGE_SIZE);
  const slice = decisions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">Allocator Decision Log</h3>
        <span className="text-xs text-gray-400">{decisions.length} entries</span>
      </div>
      {decisions.length === 0 ? (
        <p className="p-6 text-sm text-gray-400 text-center">No decisions recorded yet.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-right">Meta %</th>
                  <th className="px-4 py-3 text-right">Google Ads %</th>
                  <th className="px-4 py-3 text-right">LSA %</th>
                  <th className="px-4 py-3 text-left">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {slice.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {new Date(d.decidedAt).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-3 text-right">{d.metaPct}%</td>
                    <td className="px-4 py-3 text-right">{d.googlePct}%</td>
                    <td className="px-4 py-3 text-right">{d.lsaPct}%</td>
                    <td className="px-4 py-3 text-gray-600 text-xs max-w-xs truncate">
                      {d.rebalanceReason || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ManualOverridePanel({
  report,
  onOverrideApplied,
}: {
  report: PerNetworkReport;
  onOverrideApplied: () => void;
}) {
  const { user } = useUser();
  const isGrowthAdmin = user?.publicMetadata?.is_platform_admin === true;

  const initial = report.currentStrategy ?? { metaPct: 0, googlePct: 0, lsaPct: 0, mode: 'rules' as const };
  const [metaPct, setMetaPct] = useState(initial.metaPct);
  const [googlePct, setGooglePct] = useState(initial.googlePct);
  const [lsaPct, setLsaPct] = useState(initial.lsaPct);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = metaPct + googlePct + lsaPct;

  const handleApply = async () => {
    if (total !== 100) {
      setError('Percentages must sum to 100.');
      return;
    }
    if (!user?.id) return;
    setIsSaving(true);
    setError(null);
    try {
      await applyAllocationOverride(
        report.businessId,
        { metaPct, googlePct, lsaPct },
        user.id
      );
      onOverrideApplied();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to apply override');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isGrowthAdmin) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Manual Override</h3>
        <p className="text-sm text-gray-500">Elevated <code className="text-xs bg-gray-100 px-1 rounded">growth_admin</code> permission required.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <h3 className="text-sm font-semibold text-amber-800 mb-3">Manual Allocation Override</h3>
      <p className="text-xs text-amber-700 mb-4">
        Overrides will be applied immediately and logged as a manual decision.
        The nightly rebalancer will resume normal operation next run unless re-overridden.
      </p>
      <div className="grid grid-cols-3 gap-3 mb-3">
        {[
          { label: 'Meta %', value: metaPct, set: setMetaPct },
          { label: 'Google Ads %', value: googlePct, set: setGooglePct },
          { label: 'LSA %', value: lsaPct, set: setLsaPct },
        ].map(({ label, value, set }) => (
          <div key={label}>
            <label className="block text-xs font-medium text-amber-800 mb-1">{label}</label>
            <input
              type="number"
              min={0}
              max={100}
              value={value}
              onChange={(e) => set(Math.max(0, Math.min(100, Number(e.target.value))))}
              className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
        ))}
      </div>
      <div className={`text-xs mb-3 font-medium ${total === 100 ? 'text-green-700' : 'text-red-600'}`}>
        Total: {total}% {total !== 100 && '(must equal 100)'}
      </div>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <button
        onClick={handleApply}
        disabled={isSaving || total !== 100}
        className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50"
      >
        {isSaving ? 'Applying…' : 'Apply Override'}
      </button>
    </div>
  );
}

function NetworkControlsPanel({
  report,
}: {
  report: PerNetworkReport;
}) {
  const { user } = useUser();
  const { getToken } = useAuth();
  const isGrowthAdmin = user?.publicMetadata?.is_platform_admin === true;
  const [loading, setLoading] = useState<AdNetwork | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isGrowthAdmin) return null;

  const networks: AdNetwork[] = ['meta', 'google_ads', 'google_lsa'];

  const handleToggle = async (network: AdNetwork, paused: boolean) => {
    setLoading(network);
    setError(null);
    try {
      const token = await getToken({ template: 'supabase' });
      if (!token) throw new Error('Auth token unavailable');
      await setNetworkPaused(report.businessId, network, paused, token);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-800 mb-3">Network Controls</h3>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <div className="space-y-2">
        {networks.map((network) => {
          const networkCampaigns = report.campaigns.filter((c) => c.network === network);
          const hasActive = networkCampaigns.some((c) => c.status === 'active');
          const allPaused = networkCampaigns.length > 0 && networkCampaigns.every((c) => c.status === 'paused');
          const isLoading = loading === network;

          return (
            <div key={network} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2">
                <NetworkBadge network={network} />
                <span className="text-xs text-gray-500">
                  {networkCampaigns.length === 0 ? 'No campaigns' : allPaused ? 'All paused' : hasActive ? 'Active' : 'Draft/Archived'}
                </span>
              </div>
              {networkCampaigns.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => handleToggle(network, false)}
                    disabled={isLoading || hasActive}
                    className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-40"
                  >
                    {isLoading ? '…' : 'Resume'}
                  </button>
                  <button
                    onClick={() => handleToggle(network, true)}
                    disabled={isLoading || allPaused}
                    className="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 disabled:opacity-40"
                  >
                    {isLoading ? '…' : 'Pause'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function PerNetworkReportPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const [report, setReport] = useState<PerNetworkReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logPage, setLogPage] = useState(1);

  useSetPageMeta({
    title: 'Per-Network Report',
    description: 'Per-network campaign metrics, CPAQ, and allocator decisions',
  });

  const loadReport = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchPerNetworkReport(businessId);
      setReport(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-lg bg-gray-100" />
        ))}
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <p className="text-sm text-red-700">{error ?? 'Report unavailable'}</p>
        <button
          onClick={loadReport}
          className="mt-3 text-sm text-red-600 underline hover:text-red-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/growth-ops"
            className="text-xs text-gray-500 hover:text-gray-700 mb-1 inline-flex items-center gap-1"
          >
            ← Growth Ops
          </Link>
          <h1 className="text-lg font-semibold text-gray-900">{report.businessName}</h1>
          <p className="text-sm text-gray-500">Per-network reporting</p>
        </div>
        {report.currentStrategy && (
          <div className="text-right text-xs text-gray-500">
            <p className="font-medium text-gray-700">Current Allocation</p>
            <p>Meta {report.currentStrategy.metaPct}% · Google {report.currentStrategy.googlePct}% · LSA {report.currentStrategy.lsaPct}%</p>
            <p className="capitalize">Mode: {report.currentStrategy.mode}</p>
          </div>
        )}
      </div>

      <CampaignTable report={report} />
      <CpaqTable report={report} />
      <AllocatorLog
        decisions={report.allocatorDecisions}
        page={logPage}
        onPageChange={setLogPage}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ManualOverridePanel report={report} onOverrideApplied={loadReport} />
        <NetworkControlsPanel report={report} />
      </div>
    </div>
  );
}
